@if (label) {
	<ic-label [help]="help" [id]="id" [missing]="missing" [missingText]="missingText" [required]="required">
		{{ label }}
	</ic-label>
}

<div class="select-input" [class.disabled]="disabled" [class.has-icon]="icon" [formGroup]="innerForm">
	@if (icon) {
		<i aria-hidden="true" [ngClass]="[icon, 'icon']"> </i>
	}
	<span class="input" [class.is-hidden]="(showList$ | async) || !value" [class.is-placeholder]="selected.size === 0">
		@if (placeholderTemplate && value && selectedValue) {
			<ng-template [ngTemplateOutlet]="placeholderTemplate" [ngTemplateOutletContext]="{ $implicit: selectedValue }" />
		} @else {
			@if (disabledValue) {
				{{ disabledValue }}
			} @else {
				{{ fallbackPlaceholder }}
			}
		}
	</span>
	<input
		#input
		aria-autocomplete="list"
		data-test="search-input"
		formControlName="value"
		role="combobox"
		type="text"
		[attr.aria-controls]="(showList$ | async) ? id + '-listbox' : null"
		[attr.aria-expanded]="(showList$ | async) ? 'true' : 'false'"
		[attr.disabled]="disabled || null"
		[class.has-options]="items?.length"
		[class.is-hidden]="(showList$ | async) !== true && value"
		[id]="id"
		[placeholder]="placeholder || ''"
		(focus)="onFocus()"
		(keyup)="handleKeyUp($event)"
	/>
	<i aria-hidden="true" class="caret" [ngClass]="actionIcon"> </i>
</div>
@if (showList$ | async) {
	<div #searchMenu class="select-options" data-test="select-options">
		<ul #list role="listbox" tabindex="0" [attr.aria-multiselectable]="multi" [id]="id + '-listbox'">
			@if (selectedItems.length > 0) {
				<div class="selected" data-test="choices" role="group">
					@if (selectedTemplate) {
						@for (item of selectedItems; track item) {
							<li
								#listItem
								aria-selected="true"
								role="option"
								tabindex="0"
								[attr.data-test]="'chosen-' + itemDataTest(item)"
								(click)="selectOption(item)"
								(keyup)="handleKeyUp($event)"
							>
								<ng-template [ngTemplateOutlet]="selectedTemplate" [ngTemplateOutletContext]="{ $implicit: item }" />
							</li>
						}
					} @else {
						@for (item of selectedItems; track item) {
							<li
								#listItem
								aria-selected="true"
								class="selected-item"
								role="option"
								tabindex="0"
								[attr.data-test]="'chosen-' + itemDataTest(item)"
								(click)="selectOption(item)"
								(keyup)="handleKeyUp($event)"
							>
								<span>
									@if (itemTemplate) {
										<ng-template [ngTemplateOutlet]="itemTemplate" [ngTemplateOutletContext]="{ $implicit: item }" />
									} @else {
										{{ item }}
									}
								</span>
								<i class="fa fa-times deselect-icon"> </i>
							</li>
						}
					}
				</div>
			}

			@if ((loading$ | async) || updatingItems) {
				<div class="loading" data-test="loading-indicator">
					@if (loadingTemplate) {
						<ng-template [ngTemplateOutlet]="loadingTemplate" />
					} @else {
						<p class="loading"><i class="fa fa-spinner fa-spin"></i> {{ "general.loading" | translate }}</p>
					}
				</div>
			} @else {
				@if (filteredItems && (filteredItems.length > 0 || selectedItems.length > 0)) {
					@for (item of filteredItems; track item) {
						<li
							#listItem
							aria-selected="false"
							role="option"
							tabindex="0"
							[attr.data-test]="'option-' + itemDataTest(item)"
							(click)="selectOption(item)"
							(keyup)="handleKeyUp($event)"
						>
							@if (itemTemplate) {
								<ng-template [ngTemplateOutlet]="itemTemplate" [ngTemplateOutletContext]="{ $implicit: item }" />
							} @else {
								{{ item }}
							}
						</li>
					}
				} @else {
					<li data-test="option-no-items-match-search" role="presentation">
						@if (emptyTemplate) {
							<ng-template [ngTemplateOutlet]="emptyTemplate" />
						} @else {
							{{ "general.noData" | translate }}
						}
					</li>
				}
			}

			@if (actionTemplate) {
				<!--
					This row triggers an action instead of selecting a value, so it is always aria-selected="false".
					role="option" requires the state, and the row has to stay a listbox child to be reachable with the
					arrow keys, so it is exposed as an option that simply never becomes selected.
				-->
				<li
					#listItem
					aria-selected="false"
					data-test="option-perform-action"
					role="option"
					tabindex="0"
					(click)="onAction()"
					(keyup)="handleKeyUp($event)"
				>
					<ng-template [ngTemplateOutlet]="actionTemplate" />
				</li>
			}
		</ul>
	</div>
}

<ng-content select=".errors" />
